import { Head } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'

interface PricingProps {
  starterPaymentLink: string
  professionalPaymentLink: string
  userEmail: string
}

export default function Pricing({ starterPaymentLink, professionalPaymentLink, userEmail }: PricingProps) {
  const starterLink = `${starterPaymentLink}?prefilled_email=${encodeURIComponent(userEmail)}`
  const professionalLink = `${professionalPaymentLink}?prefilled_email=${encodeURIComponent(userEmail)}`

  return (
    <>
      <Head title="Choose Your Plan - FilterHawk" />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              All plans include a 14-day free trial.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Starter
              </h2>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900 dark:text-white">$8</span>
                <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">Billed yearly</p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Unlimited emails processed</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Up to 3 workflows</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">New sender screening</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Complete audit trails</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Block list management</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Email support</span>
                </li>
              </ul>

              <Button asChild className="w-full" size="lg" variant="outline">
                <a href={starterLink}>Start Free Trial</a>
              </Button>
            </div>

            {/* Professional Plan */}
            <div className="bg-blue-600 dark:bg-blue-700 rounded-lg shadow-xl p-8 border-2 border-blue-500 relative">
              <div className="absolute top-0 right-0 bg-pink-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold">
                Most Popular
              </div>

              <h2 className="text-2xl font-bold text-white mb-4">
                Professional
              </h2>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white">$24</span>
                <span className="text-blue-100 ml-2">/month</span>
              </div>
              <p className="text-blue-100 mb-6">Billed yearly</p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-blue-200 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-white">Unlimited emails processed</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-blue-200 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-white">Up to 20 workflows</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-blue-200 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-white">Everything in Starter, plus:</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-blue-200 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-white">Priority support</span>
                </li>
              </ul>

              <Button asChild className="w-full bg-white text-blue-600 hover:bg-blue-50" size="lg">
                <a href={professionalLink}>Start Free Trial</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
